import React, {
  useState, useCallback, useEffect, useContext, useRef
} from 'react';
import PropTypes from 'prop-types';
import { Skeleton, SkeletonBlock } from '@one-thd/sui-atomic-components';
import { ProductResultsContext } from '../ProductResultsContext';
import { useDeliveryOptionsContext } from '../../context/DeliveryOptionsContext';
import { DeliveryRefinements } from './DeliveryRefinements';
import { InNearbyStoreContainer } from './InNearbyStore/InNearbyStoreContainer';
import { FulfillmentTypeSelector } from './FulfillmentTypeSelector';
import {
  BROWSE_ALL_REFINEMENT,
  GET_IT_FAST_DIMENSION_LABEL,
  FULFILLMENT_LABELS,
  FULFILLMENT_DIMENSIONS_LABELS
} from '../../utils/constants';
import {
  getFulfillmentTileLabel,
  findDimensionByLabels,
  getAppliedDeliveryDimension,
  getRefinementCount
} from '../../utils/fulfillment-helpers';

const { func, string } = PropTypes;

const SelectedFulfillmentContent = ({
  selectedFulfillment,
  onDimensionChange,
  onRefinementChange
}) => {
  switch (selectedFulfillment) {
    case FULFILLMENT_LABELS.IN_STORE:
      return <InNearbyStoreContainer />;
    case FULFILLMENT_LABELS.DELIVERY:
    case FULFILLMENT_LABELS.FREE_DELIVERY:
      return (
        <DeliveryRefinements
          className="sm:sui-flex-row sm:sui-justify-start md:sui-flex-col xl:sui-flex-row sm:sui-pt-4 sui-pt-3 xl:sui-pt-4"
          onDimensionChange={onDimensionChange}
          onRefinementChange={onRefinementChange}
        />
      );
    default:
      return null;
  }
};

SelectedFulfillmentContent.propTypes = {
  selectedFulfillment: string.isRequired,
  onDimensionChange: func.isRequired,
  onRefinementChange: func.isRequired
};

const FulfillmentOptions = ({
  onFulfillmentChange
}) => {
  const {
    appliedDimensionLabel,
    fulfillmentDimensions,
    setIsFreeDeliveryChecked,
    setTriggerDeliverySpeedChange
  } = useDeliveryOptionsContext();

  const { data } = useContext(ProductResultsContext);
  const { dimensions = [], appliedDimensions = [] } = data?.searchModel || {};
  const [isLoading, setIsLoading] = useState(true);
  const [show, setShow] = useState(false);
  const previousDimensionsLength = useRef(0);
  const decisionToRenderCompleted = useRef(false);
  const [selectedTile, setSelectedTile] = useState(
    getFulfillmentTileLabel(appliedDimensionLabel) ?? BROWSE_ALL_REFINEMENT.label
  );

  // Check if we should render the supertoggle
  const shouldRender = useCallback(() => {
    if (!dimensions || dimensions.length === 0) {
      return false;
    }

    const getItFastDim = findDimensionByLabels(dimensions, [FULFILLMENT_DIMENSIONS_LABELS.instore]);
    const deliveryDim = findDimensionByLabels(dimensions, [FULFILLMENT_DIMENSIONS_LABELS.delivery]);

    const inStoreCount = getRefinementCount(getItFastDim, 'Pick Up Today');
    const deliveryCount = getRefinementCount(deliveryDim, 'Anytime');

    return !!deliveryDim && (inStoreCount > 0 || deliveryCount > 0);
  }, [dimensions]);

  const getAnalyticsForFulfillment = (value, speed) => {
    return {
      filterType: 'primary',
      type: 'get it fast',
      value,
      ...(value === 'instore' && { nearbyStore: 'y' }),
      ...(value === 'delivery' && { speed: speed ?? 'anytime', freeDelivery: 'n' })
    };
  };

  const handleChange = useCallback(({ refinement, dimension }) => {
    if (!refinement?.label || refinement.label === selectedTile) {
      return;
    }

    const isDelivery = [FULFILLMENT_LABELS.DELIVERY, FULFILLMENT_LABELS.FREE_DELIVERY].includes(dimension?.label);
    const isBrowseAll = refinement?.label === FULFILLMENT_LABELS.BROWSE_ALL;
    setSelectedTile(isDelivery ? FULFILLMENT_LABELS.DELIVERY : refinement?.label);
    let selectedFulfillment;

    if (isDelivery) {
      selectedFulfillment = dimension?.label;
      setTriggerDeliverySpeedChange(refinement?.label);
    } else {
      selectedFulfillment = refinement?.label;
      setIsFreeDeliveryChecked(false);
      setTriggerDeliverySpeedChange(null);
    }

    document.body.classList.add('sui-opacity-50');

    if (isBrowseAll) {
      let fulfillmentDimension = appliedDimensions?.find(
        (appliedDim) => Object.values(FULFILLMENT_DIMENSIONS_LABELS).includes(appliedDim.label.toUpperCase())
      );
      // eslint-disable-next-line no-param-reassign
      refinement.url = fulfillmentDimension?.refinements[0]?.url;
    }
    setTimeout(() => {
      const analytics = getAnalyticsForFulfillment(selectedFulfillment.replace(/\s+/g, '').toLowerCase());
      const refinementWithAnalytics = {
        ...refinement,
        dimensionName: GET_IT_FAST_DIMENSION_LABEL,
        analytics,
        redirectUrl: null
      };

      onFulfillmentChange({ refinement: refinementWithAnalytics });
      document.body.classList.remove('sui-opacity-50');
    }, 0);
  }, [
    appliedDimensions,
    onFulfillmentChange,
    setIsFreeDeliveryChecked,
    setTriggerDeliverySpeedChange
  ]);

  const handleRefinementChange = useCallback((refinementAndDimension) => {
  const refinement = refinementAndDimension.refinement;
  const analytics = getAnalyticsForFulfillment(refinementAndDimension.dimension.label.toLowerCase(), refinement.label);
  const refinementWithAnalytics = {
        ...refinement,
        dimensionName: GET_IT_FAST_DIMENSION_LABEL,
        analytics,
        redirectUrl: null,
        selected: null
      };
    onFulfillmentChange({
      refinement: refinementWithAnalytics
    });
  }, [onFulfillmentChange]);

  useEffect(() => {
    const shouldRenderComponent = shouldRender();
    setShow(shouldRenderComponent);
    decisionToRenderCompleted.current = true;

    if (dimensions.length !== previousDimensionsLength.current) {
      // Set loading to true when dimensions change
      setIsLoading(true);
      // Reset the decision flag
      decisionToRenderCompleted.current = false;
    } else if (dimensions.length === 0) {
      // No dimensions, no loading
      setIsLoading(false);
    }

    previousDimensionsLength.current = dimensions.length;
  }, [shouldRender]);

  useEffect(() => {
    // Set loading to false on show changes or when shouldRender has completed its decision
    // after a non-initial render, like dimensions updates or refinement changes
    if (show || (decisionToRenderCompleted.current && isLoading)) {
      setIsLoading(false);
    }
  }, [isLoading, show]);

  useEffect(() => {
    const getItFastDimension = findDimensionByLabels(appliedDimensions, [FULFILLMENT_DIMENSIONS_LABELS.instore]);
    const deliveryDimension = getAppliedDeliveryDimension(appliedDimensions);
    if (getItFastDimension?.refinements.length > 0) {
      setSelectedTile(FULFILLMENT_LABELS.IN_STORE);
    } else if (deliveryDimension?.refinements.length > 0) {
      setSelectedTile(FULFILLMENT_LABELS.DELIVERY);
    } else {
      setSelectedTile(FULFILLMENT_LABELS.BROWSE_ALL);
    }
  }, [appliedDimensions]);

  if (isLoading) {
    return (
      <Skeleton disablePadding grow>
        <SkeletonBlock height={7} />
      </Skeleton>
    );
  }

  return show && (
    <>
      <FulfillmentTypeSelector
        selectedFulfillment={selectedTile}
        handleChange={handleChange}
        fulfillmentDimensions={fulfillmentDimensions}
      />
      <SelectedFulfillmentContent
        selectedFulfillment={selectedTile}
        onDimensionChange={handleChange}
        onRefinementChange={handleRefinementChange}
      />
    </>
  );
};

FulfillmentOptions.propTypes = {
  onFulfillmentChange: func.isRequired
};

FulfillmentOptions.displayName = 'FulfillmentOptions';

export { FulfillmentOptions };